import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import {
  listAttributeMappings,
  createAttributeMapping,
  updateMappingPriority,
  deleteAttributeMapping,
  listPromptTemplates,
} from '../services/api';
import Header from './Header';

interface AttributeMapping {
  mapping_id: string;
  attribute_name: string;
  attribute_value: string;
  template_type: 'WP' | 'CR' | 'SYSTEM';
  template_id: string;
  template_name: string;
  priority: number;
  assigned_at: string;
  assigned_by: string;
  status: 'active' | 'inactive';
}

interface PromptTemplate {
  template_id: string;
  template_name: string;
  template_type: string;
}

const AttributeMappingTable: React.FC = () => {
  const [mappings, setMappings] = useState<AttributeMapping[]>([]);
  const [filteredMappings, setFilteredMappings] = useState<AttributeMapping[]>([]);
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('all');
  const [createDialogOpen, setCreateDialogOpen] = useState<boolean>(false);
  const [editPriorityDialogOpen, setEditPriorityDialogOpen] = useState<boolean>(false);
  const [editingMapping, setEditingMapping] = useState<AttributeMapping | null>(null);
  const [newPriority, setNewPriority] = useState<number>(100);

  const [formData, setFormData] = useState({
    attribute_name: '',
    attribute_value: '',
    template_type: 'WP' as 'WP' | 'CR' | 'SYSTEM',
    template_id: '',
    priority: 100,
  });

  useEffect(() => {
    loadMappings();
  }, []);

  useEffect(() => {
    filterMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappings, searchQuery, filterType]);

  const loadMappings = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAttributeMappings();
      setMappings(data.mappings || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load mappings');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async (type: string) => {
    try {
      const data = await listPromptTemplates(type);
      setTemplates(data.templates || []);
    } catch (err: any) {
      console.error('Failed to load templates:', err);
      setTemplates([]);
    }
  };

  const filterMappings = () => {
    let filtered = mappings;

    if (filterType !== 'all') {
      filtered = filtered.filter((m) => m.template_type === filterType);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (m) =>
          m.attribute_name.toLowerCase().includes(query) ||
          m.attribute_value.toLowerCase().includes(query) ||
          m.template_name.toLowerCase().includes(query)
      );
    }

    setFilteredMappings(filtered.sort((a, b) => a.priority - b.priority));
  };

  const handleCreateMapping = () => {
    setFormData({
      attribute_name: '',
      attribute_value: '',
      template_type: 'WP',
      template_id: '',
      priority: 100,
    });
    loadTemplates('WP');
    setCreateDialogOpen(true);
  };

  const handleTypeChange = (type: string) => {
    setFormData((prev) => ({
      ...prev,
      template_type: type as 'WP' | 'CR' | 'SYSTEM',
      template_id: '',
    }));
    loadTemplates(type);
  };

  const handleSubmitCreate = async () => {
    setError(null);

    if (!formData.attribute_name.trim()) {
      setError('Attribute name is required');
      return;
    }

    if (!formData.attribute_value.trim()) {
      setError('Attribute value is required');
      return;
    }

    if (!formData.template_id) {
      setError('Template selection is required');
      return;
    }

    setLoading(true);
    try {
      await createAttributeMapping({
        attribute_name: formData.attribute_name,
        attribute_value: formData.attribute_value,
        template_type: formData.template_type,
        template_id: formData.template_id,
        priority: formData.priority,
      });
      setCreateDialogOpen(false);
      loadMappings();
    } catch (err: any) {
      setError(err.message || 'Failed to create mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPriority = (mapping: AttributeMapping) => {
    setEditingMapping(mapping);
    setNewPriority(mapping.priority);
    setEditPriorityDialogOpen(true);
  };

  const handleSubmitPriorityEdit = async () => {
    if (!editingMapping) return;

    setLoading(true);
    setError(null);
    try {
      await updateMappingPriority(editingMapping.mapping_id, newPriority);
      setEditPriorityDialogOpen(false);
      loadMappings();
    } catch (err: any) {
      setError(err.message || 'Failed to update priority');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteMapping = async (mappingId: string) => {
    if (!window.confirm('Are you sure you want to delete this mapping?')) {
      return;
    }

    setLoading(true);
    setError(null);
    try {
      await deleteAttributeMapping(mappingId);
      loadMappings();
    } catch (err: any) {
      setError(err.message || 'Failed to delete mapping');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (isoString: string) => {
    return new Date(isoString).toLocaleString();
  };

  return (
    <>
      <Header />
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1">
            Attribute Mappings
          </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleCreateMapping}
        >
          Create New Mapping
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            placeholder="Search by attribute name, value, or template..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Filter by Type"
            >
              <MenuItem value="all">All Types</MenuItem>
              <MenuItem value="WP">WP</MenuItem>
              <MenuItem value="CR">CR</MenuItem>
              <MenuItem value="SYSTEM">SYSTEM</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredMappings.length === 0 ? (
          <Box sx={{ textAlign: 'center', p: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery || filterType !== 'all'
                ? 'No mappings match your filters.'
                : 'No attribute mappings created yet.'}
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Attribute Name</TableCell>
                  <TableCell>Attribute Value</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Template</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Assigned At</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredMappings.map((mapping) => (
                  <TableRow key={mapping.mapping_id}>
                    <TableCell>
                      <Chip label={mapping.attribute_name} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {mapping.attribute_value}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={mapping.template_type} size="small" color="primary" />
                    </TableCell>
                    <TableCell>{mapping.template_name}</TableCell>
                    <TableCell>
                      <Chip label={mapping.priority} size="small" />
                    </TableCell>
                    <TableCell>{formatDate(mapping.assigned_at)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit Priority">
                          <IconButton size="small" onClick={() => handleEditPriority(mapping)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteMapping(mapping.mapping_id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Create Mapping Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Attribute Mapping</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Attribute Name"
              value={formData.attribute_name}
              onChange={(e) => setFormData({ ...formData, attribute_name: e.target.value })}
              fullWidth
              required
              helperText="e.g., 'isn', 'district_id', 'school_id'"
            />
            <TextField
              label="Attribute Value"
              value={formData.attribute_value}
              onChange={(e) => setFormData({ ...formData, attribute_value: e.target.value })}
              fullWidth
              required
              helperText="e.g., 'hisd', '2700001'"
            />
            <FormControl fullWidth>
              <InputLabel>Template Type</InputLabel>
              <Select
                value={formData.template_type}
                onChange={(e) => handleTypeChange(e.target.value)}
                label="Template Type"
              >
                <MenuItem value="WP">WP (Writing Prompt)</MenuItem>
                <MenuItem value="CR">CR (Constructed Response)</MenuItem>
                <MenuItem value="SYSTEM">SYSTEM (System Prompt)</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Template</InputLabel>
              <Select
                value={formData.template_id}
                onChange={(e) => setFormData({ ...formData, template_id: e.target.value })}
                label="Template"
              >
                {templates.map((t) => (
                  <MenuItem key={t.template_id} value={t.template_id}>
                    {t.template_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Priority"
              type="number"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 100 })}
              fullWidth
              helperText="Lower number = higher priority (default: 100)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmitCreate} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Create Mapping'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Priority Dialog */}
      <Dialog
        open={editPriorityDialogOpen}
        onClose={() => setEditPriorityDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Edit Priority</DialogTitle>
        <DialogContent>
          <TextField
            label="Priority"
            type="number"
            value={newPriority}
            onChange={(e) => setNewPriority(parseInt(e.target.value) || 100)}
            fullWidth
            sx={{ mt: 2 }}
            helperText="Lower number = higher priority"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPriorityDialogOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmitPriorityEdit} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
      </Container>
    </>
  );
};

export default AttributeMappingTable;
